import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Copy, Mail, Printer, DollarSign, Lock, AlertTriangle, Loader2, Users, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// US-style date display (MM/DD/YYYY)
const usDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const todayChicagoMonday = () => {
  const now = new Date();
  const cs = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
  const dow = cs.getDay();
  const off = dow === 0 ? -6 : 1 - dow;
  cs.setDate(cs.getDate() + off);
  return `${cs.getFullYear()}-${String(cs.getMonth() + 1).padStart(2, "0")}-${String(cs.getDate()).padStart(2, "0")}`;
};
const addWeeks = (s, w) => {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + w * 7);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const monthYearOf = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleString("en-US", { month: "long" }).toUpperCase() + " --- " + y;
};
const dateOf = (mon, idx) => {
  const [y, m, d] = mon.split("-").map(Number);
  const dt = new Date(y, m - 1, d + idx);
  return dt.getDate();
};

const fmtSimple = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  let hh = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hh}:${String(m).padStart(2, "0")}`;
};

const hoursBetween = (a, b) => {
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  let diff = (bh * 60 + bm) - (ah * 60 + am);
  if (diff <= 0) diff += 1440;
  return diff / 60;
};

const DEPARTMENTS = ["Admin", "Meat & Fish", "Deli", "Grocery", "Produce", "Cashier", "Frozen/Dairy", "Other"];
const DEPT_COLORS = {
  Admin: "bg-orange-100 text-orange-900",
  "Meat & Fish": "bg-red-100 text-red-900",
  Grocery: "bg-purple-100 text-purple-900",
  Produce: "bg-green-100 text-green-900",
  Deli: "bg-yellow-100 text-yellow-900",
  Cashier: "bg-pink-100 text-pink-900",
  "Frozen/Dairy": "bg-blue-100 text-blue-900",
  Other: "bg-gray-100 text-gray-900",
};

// Lunch room print layout — 1 page per group, dept names merged where indicated.
// Footer banner text printed bottom of each page (red/yellow highlight). null = no footer.
// footer can be a string or array of strings (each item renders on its own line in the banner).
const PRINT_GROUPS = [
  { title: "MEAT & FISH",                 depts: ["Meat & Fish"],                 footer: ["NO CELL PHONES", "ALL LUNCH BREAKS — HALF HOUR"] },
  { title: "DELI + FROZEN/DAIRY + GROCERY", depts: ["Deli", "Frozen/Dairy", "Grocery"], footer: null },
  { title: "PRODUCE",                     depts: ["Produce"],                     footer: "FOLLOW SCHEDULE CLOSELY or 2-DAYS OFF" },
  { title: "CASHIER",                     depts: ["Cashier"],                     footer: ["NO CELL PHONES", "ALL LUNCH BREAKS — HALF HOUR"] },
  { title: "ADMIN",                       depts: ["Admin"],                       footer: ["NO CELL PHONES", "ALL LUNCH BREAKS — HALF HOUR"] },
];

export default function SchedulePage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [weekStart, setWeekStart] = useState(todayChicagoMonday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [budgetInput, setBudgetInput] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [hiddenList, setHiddenList] = useState([]);
  const [showHiddenModal, setShowHiddenModal] = useState(false);

  const loadHidden = async () => {
    try {
      const res = await axios.get(`${API}/timeclock/employees/hidden`);
      setHiddenList(res.data || []);
    } catch { /* silent */ }
  };

  const hideEmployee = async (code, name) => {
    if (!window.confirm(`Hide ${name} from the schedule? They will still get paid + can punch in — they just won't appear on this grid. You can bring them back anytime via "Hidden Employees".`)) return;
    try {
      await axios.patch(`${API}/timeclock/employees/${code}/hide-from-schedule`, null, { params: { hidden: true } });
      toast.success(`${name} hidden from schedule`);
      loadWeek();
      loadHidden();
    } catch {
      toast.error("Failed");
    }
  };

  const unhideEmployee = async (code, name) => {
    try {
      await axios.patch(`${API}/timeclock/employees/${code}/hide-from-schedule`, null, { params: { hidden: false } });
      toast.success(`${name} back on schedule`);
      loadWeek();
      loadHidden();
    } catch {
      toast.error("Failed");
    }
  };

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/timeclock/master-login`, { password: pin });
      setLoggedIn(true);
      localStorage.setItem("schedule_master_ok", "1");
    } catch { toast.error("Invalid master PIN"); }
  };
  useEffect(() => { if (localStorage.getItem("schedule_master_ok") === "1") setLoggedIn(true); }, []);

  const loadWeek = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedule/week`, { params: { week_start: weekStart } });
      setData(res.data);
      setBudgetInput(res.data.summary.weekly_budget > 0 ? String(res.data.summary.weekly_budget) : "");
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (loggedIn) { loadWeek(); loadHidden(); } /* eslint-disable-next-line */ }, [loggedIn, weekStart]);

  const shiftsMap = useMemo(() => {
    const m = {};
    if (data) data.shifts.forEach(s => { m[`${s.employee_code}-${s.day_of_week}`] = s; });
    return m;
  }, [data]);

  const employeesByDept = useMemo(() => {
    const g = {};
    DEPARTMENTS.forEach(d => g[d] = []);
    if (data) data.employees.forEach(e => { (g[e.department || "Other"] = g[e.department || "Other"] || []).push(e); });
    return g;
  }, [data]);

  const openCell = (empCode, dayIdx) => {
    const existing = shiftsMap[`${empCode}-${dayIdx}`];
    setEditingShift(existing
      ? { ...existing }
      : { id: null, employee_code: empCode, day_of_week: dayIdx, start_time: "09:00", end_time: "17:00", status: "scheduled", note: "" }
    );
  };

  const saveShift = async () => {
    try {
      if (editingShift.id) {
        await axios.put(`${API}/schedule/shifts/${editingShift.id}`, {
          start_time: editingShift.start_time, end_time: editingShift.end_time,
          status: editingShift.status, note: editingShift.note,
        });
      } else {
        await axios.post(`${API}/schedule/shifts`, { week_start: weekStart, ...editingShift });
      }
      setEditingShift(null);
      loadWeek();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      const status = err?.response?.status;
      toast.error(`Save failed${status ? ` (${status})` : ""}: ${detail}`);
      console.error("[saveShift] failed:", err);
    }
  };

  const deleteShift = async () => {
    if (!editingShift?.id) return setEditingShift(null);
    await axios.delete(`${API}/schedule/shifts/${editingShift.id}`);
    setEditingShift(null);
    loadWeek();
  };

  const setStatus = async (status) => {
    setEditingShift({ ...editingShift, status });
  };

  const copyPrevWeek = async (employeeCode = null) => {
    const label = employeeCode
      ? `${data.employees.find(e => e.code === employeeCode)?.name}'s`
      : "EVERYONE's";
    if (!window.confirm(`Repeat last week's schedule for ${label}? ${employeeCode ? "Their" : "All"} existing shifts in this week will be replaced. You can edit any day afterwards.`)) return;
    try {
      const params = { target_week_start: weekStart };
      if (employeeCode) params.employee_code = employeeCode;
      const res = await axios.post(`${API}/schedule/copy-week`, null, { params });
      toast.success(`✓ ${res.data.copied} shifts copied. Edit any day as needed.`);
      loadWeek();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveBudget = async () => {
    try {
      await axios.put(`${API}/schedule/budget`, null, { params: { week_start: weekStart, weekly_budget: parseFloat(budgetInput) || 0 } });
      toast.success("Budget saved");
      loadWeek();
    } catch { toast.error("Failed"); }
  };

  const emailSchedule = async () => {
    setEmailing(true);
    try {
      await axios.post(`${API}/schedule/email`, { week_start: weekStart, recipients: [] });
      toast.success("Schedule emailed to staff");
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
    finally { setEmailing(false); }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A1A] to-[#2D2D2D] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#A61B1B] rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-white" /></div>
            <h2 className="text-2xl font-bold">Schedule Builder</h2>
          </div>
          <Input type="password" placeholder="Master PIN" value={pin} onChange={(e) => setPin(e.target.value)} className="text-center text-2xl font-bold h-14 mb-4" onKeyPress={(e) => e.key === "Enter" && handleLogin()} />
          <Button onClick={handleLogin} className="w-full bg-[#A61B1B] h-12 text-lg">Continue</Button>
          <p className="text-center text-xs text-[#666] mt-4"><Link to="/timecard" className="hover:underline">← Back to PayPunch</Link></p>
        </div>
      </div>
    );
  }

  const overBudget = data?.summary?.over_budget;

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <header className="bg-[#1A1A1A] text-white py-3 px-4 print:hidden">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/timecard" className="text-white hover:text-[#D4AF37] flex items-center gap-2" data-testid="schedule-back-to-timecard"><ArrowLeft className="w-5 h-5" /><span className="font-bold">Schedule Builder</span></Link>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => window.location.href = "/timecard"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1" data-testid="schedule-nav-timecard"><Clock className="w-4 h-4" />Time Clock</Button>
            <Button size="sm" onClick={() => window.location.href = "/metrics"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1" data-testid="schedule-nav-metrics"><TrendingUp className="w-4 h-4" />Metrics</Button>
            <Button size="sm" onClick={() => window.location.href = "/kiosk"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1" data-testid="schedule-nav-kiosk"><Lock className="w-4 h-4" />Kiosk</Button>
            <Button size="sm" onClick={() => window.print()} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1"><Printer className="w-4 h-4" />Print</Button>
            <Button size="sm" onClick={emailSchedule} disabled={emailing} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1">{emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Email</Button>
            <Button size="sm" variant="outline" onClick={() => { localStorage.removeItem("schedule_master_ok"); setLoggedIn(false); }} className="border-white text-white hover:bg-white hover:text-[#1A1A1A]">Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-3 print:p-0">
        <div className="bg-white rounded-lg p-3 mb-3 print:hidden flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setWeekStart(addWeeks(weekStart, -1))}>← Prev</Button>
          <Button size="sm" variant="outline" onClick={() => setWeekStart(todayChicagoMonday())}>This Week</Button>
          <Button size="sm" variant="outline" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>Next →</Button>
          <span className="font-bold text-[#1A1A1A] ml-2">{usDate(data?.week_start)} → {usDate(data?.week_end)}</span>
          <Button
            size="sm"
            onClick={() => copyPrevWeek()}
            className="gap-1 ml-auto bg-[#1A5E1F] hover:bg-[#0F4014] text-white shadow-md"
            data-testid="btn-repeat-last-week"
            title="Copy entire last week's schedule into this week — you can still edit individual days after"
          >
            <Copy className="w-4 h-4" />
            Repeat Last Week
          </Button>
        </div>

        {data && (
          <div className={`rounded-lg p-3 mb-3 print:hidden ${overBudget ? "bg-red-50 border border-[#A61B1B]" : "bg-white"}`}>
            <div className="flex flex-wrap items-center gap-4 text-sm mb-2">
              <div><span className="text-[#666]">Hours:</span> <span className="font-bold text-lg ml-1">{data.summary.total_hours}h</span></div>
              <div><span className="text-[#666]">Projected cost:</span> <span className={`font-bold text-lg ml-1 ${overBudget ? "text-[#A61B1B]" : "text-[#1A5E1F]"}`}>${data.summary.total_cost.toFixed(2)}</span></div>
              <div className="flex items-center gap-2 ml-auto">
                {hiddenList.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setShowHiddenModal(true)} className="border-amber-500 text-amber-700 hover:bg-amber-50 h-8" data-testid="btn-show-hidden">
                    👁️‍🗨️ Hidden ({hiddenList.length})
                  </Button>
                )}
                <DollarSign className="w-4 h-4 text-[#666]" />
                <span className="text-xs text-[#666]">Budget:</span>
                <Input type="number" step="50" value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} className="w-28 h-8 text-sm" placeholder="e.g. 4500" />
                <Button size="sm" onClick={saveBudget} className="bg-[#1A5E1F] h-8">Save</Button>
              </div>
            </div>
            {overBudget && (
              <div className="flex items-center gap-2 text-[#A61B1B] font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Over by ${(data.summary.total_cost - data.summary.weekly_budget).toFixed(2)}</div>
            )}
            {/* Per-department cost row */}
            <div className="flex flex-wrap gap-2 mt-2">
              {DEPARTMENTS.filter(d => data.summary.by_department[d]?.cost > 0 || data.summary.by_department[d]?.hours > 0).map(d => (
                <div key={d} className={`${DEPT_COLORS[d]} rounded px-2 py-1 text-xs`}>
                  <span className="font-bold uppercase">{d}:</span> {data.summary.by_department[d].hours}h · <span className="font-bold">${data.summary.by_department[d].cost.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Printable schedule grid */}
        {loading ? (
          <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : !data ? null : (
          <>
          {/* === ON-SCREEN view (manager): single big table with $ info, dept color bands === */}
          <div className="bg-white rounded-none p-2 print:hidden border border-black">
            {/* Header band: MONTH --- YEAR | dates 1-7 (orange) */}
            <table className="w-full border-collapse text-sm" style={{ fontFamily: "Arial, sans-serif" }}>
              <thead>
                <tr>
                  <th className="border border-black bg-[#FFE066] px-3 py-2 text-left font-bold uppercase" style={{ minWidth: "140px" }}>{data.week_start && monthYearOf(data.week_start)}</th>
                  {DAYS.map((_, i) => (
                    <th key={i} className="border border-black bg-[#FFA500] px-2 py-1 text-center font-bold" style={{ minWidth: "100px" }}>{data.week_start && dateOf(data.week_start, i)}</th>
                  ))}
                </tr>
                <tr>
                  <th className="border border-black bg-white"></th>
                  {DAYS.map((d) => <th key={d} className="border border-black bg-white px-2 py-1 text-center underline font-bold text-xs">{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {DEPARTMENTS.map(dept => {
                  const emps = employeesByDept[dept] || [];
                  if (emps.length === 0) return null;
                  const summary = data.summary.by_department[dept] || { hours: 0, cost: 0 };
                  const empsWithShifts = emps.filter(e => DAYS.some((_, i) => shiftsMap[`${e.code}-${i}`]));
                  const deptHasShifts = empsWithShifts.length > 0;
                  return (
                    <DeptGroup
                      key={`dept-${dept}`}
                      dept={dept}
                      emps={emps}
                      summary={summary}
                      shiftsMap={shiftsMap}
                      DAYS={DAYS}
                      openCell={openCell}
                      copyPrevWeek={copyPrevWeek}
                      fmtSimple={fmtSimple}
                      hideEmployee={hideEmployee}
                      deptHasShifts={deptHasShifts}
                      scheduledCount={empsWithShifts.length}
                    />
                  );
                })}
              </tbody>
            </table>
            <div className="bg-[#FFE066] border border-black mt-2 py-2 px-3 text-[#1A1A1A] text-xs">
              <div className="font-extrabold text-center uppercase text-sm text-[#A61B1B] mb-1">Lunch Break Rules</div>
              <div className="flex items-center justify-around flex-wrap gap-x-4 gap-y-1 font-bold">
                <span><span className="text-[#1A5E1F]">Under 6 hrs</span> → No break</span>
                <span><span className="text-[#92400E]">6 to 7 hrs</span> → 20 min break</span>
                <span><span className="text-[#A61B1B]">7 hrs &amp; above</span> → 30 min break</span>
              </div>
            </div>
          </div>

          {/* === PRINT-ONLY view: 1 page per group, lunch-room friendly, no $ values === */}
          <div className="hidden print:block">
            {PRINT_GROUPS.map((group, gIdx) => {
              const pageEmps = group.depts.flatMap((dept, dIdx) => {
                const emps = (employeesByDept[dept] || []).filter(e => DAYS.some((_, i) => shiftsMap[`${e.code}-${i}`]));
                return emps.map(e => ({ ...e, _deptIdx: dIdx }));
              });
              if (pageEmps.length === 0) return null;
              const isLast = gIdx === PRINT_GROUPS.length - 1;
              return (
                <div key={group.title} className="print-page" style={{ pageBreakAfter: isLast ? "auto" : "always", paddingBottom: "12px" }}>
                  <div className="text-center font-extrabold uppercase tracking-wider mb-3" style={{ fontSize: "20pt", letterSpacing: "2px" }}>{group.title}</div>
                  <table className="w-full border-collapse" style={{ fontFamily: "Arial, sans-serif" }}>
                    <thead>
                      <tr>
                        <th className="border border-black bg-[#FFE066] px-3 py-2 text-left font-bold uppercase" style={{ minWidth: "160px", fontSize: "13pt" }}>{data.week_start && monthYearOf(data.week_start)}</th>
                        {DAYS.map((_, i) => (
                          <th key={i} className="border border-black bg-[#FFA500] px-2 py-2 text-center font-bold" style={{ fontSize: "13pt" }}>{data.week_start && dateOf(data.week_start, i)}</th>
                        ))}
                      </tr>
                      <tr>
                        <th className="border border-black bg-white"></th>
                        {DAYS.map((d) => <th key={d} className="border border-black bg-white px-2 py-1 text-center underline font-bold" style={{ fontSize: "11pt" }}>{d}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {pageEmps.map((emp, idx) => {
                        const prev = pageEmps[idx - 1];
                        const insertSep = prev && prev._deptIdx !== emp._deptIdx;
                        return (
                          <React.Fragment key={emp.code}>
                            {insertSep && (
                              <tr>
                                <td colSpan={8} className="border-0" style={{ height: "18px" }}></td>
                              </tr>
                            )}
                            <tr>
                              <td className="border border-black px-3 py-2 font-bold uppercase" style={{ fontSize: "13pt" }}>{emp.name}</td>
                              {DAYS.map((_, dayIdx) => {
                                const s = shiftsMap[`${emp.code}-${dayIdx}`];
                                let content = "";
                                let cellStyle = "border border-black px-1 py-0.5 text-center text-sm";
                                if (!s) {
                                  content = "";
                                } else if (s.status === "off") {
                                  content = <span className="font-black tracking-widest">OFF</span>;
                                  cellStyle += " bg-gray-100";
                                } else if (s.status === "sick") {
                                  content = <span className="font-extrabold">SICK</span>;
                                } else if (s.status === "call_off") {
                                  content = <span className="font-extrabold">CALL OFF</span>;
                                } else {
                                  content = <span className="font-medium whitespace-nowrap">{fmtSimple(s.start_time)}-{fmtSimple(s.end_time)}</span>;
                                }
                                return <td key={dayIdx} className={cellStyle}>{content}</td>;
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {group.footer && (
                    <div className="mt-3 border-2 border-black bg-[#FFF200] px-4 py-3 text-center font-extrabold uppercase text-[#A61B1B]" style={{ letterSpacing: "1px", fontSize: "14pt", lineHeight: "1.4" }}>
                      {Array.isArray(group.footer)
                        ? group.footer.map((line, i) => <div key={i}>{line}</div>)
                        : group.footer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Hidden employees modal */}
      {showHiddenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHiddenModal(false)}>
          <div className="bg-white rounded-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="hidden-modal">
            <h3 className="font-bold text-lg mb-1">Hidden Employees ({hiddenList.length})</h3>
            <p className="text-xs text-[#666] mb-4">These employees are hidden from the schedule grid but still in the system (pay + punches still work). Tap "Show" to bring them back.</p>
            {hiddenList.length === 0 ? (
              <p className="text-center text-sm text-stone-400 py-6">No one is hidden right now.</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {hiddenList.map(emp => (
                  <div key={emp.code} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <div>
                      <div className="font-bold">{emp.name}</div>
                      <div className="text-xs text-stone-500">#{emp.code} · {emp.department || "Other"}</div>
                    </div>
                    <Button size="sm" onClick={() => unhideEmployee(emp.code, emp.name)} className="bg-[#1A5E1F]" data-testid={`unhide-${emp.code}`}>Show</Button>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={() => setShowHiddenModal(false)} variant="outline" className="w-full mt-4">Close</Button>
          </div>
        </div>
      )}

      {/* Shift edit modal */}
      {editingShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full">
            <h3 className="font-bold text-lg mb-1">{DAYS[editingShift.day_of_week]} — #{editingShift.employee_code}</h3>
            <p className="text-xs text-[#666] mb-3">Set hours or mark off/sick/call-off</p>

            <div className="grid grid-cols-3 gap-1 mb-3">
              <Button size="sm" variant={editingShift.status === "scheduled" ? "default" : "outline"} onClick={() => setStatus("scheduled")} className={editingShift.status === "scheduled" ? "bg-[#1A5E1F]" : ""}>Work</Button>
              <Button size="sm" variant={editingShift.status === "off" ? "default" : "outline"} onClick={() => setStatus("off")} className={editingShift.status === "off" ? "bg-gray-700" : ""}>OFF</Button>
              <Button size="sm" variant={editingShift.status === "sick" ? "default" : "outline"} onClick={() => setStatus("sick")} className={editingShift.status === "sick" ? "bg-[#A61B1B]" : ""}>Sick</Button>
              <Button size="sm" variant={editingShift.status === "call_off" ? "default" : "outline"} onClick={() => setStatus("call_off")} className={editingShift.status === "call_off" ? "bg-[#92400E]" : ""}>Call Off</Button>
            </div>

            {editingShift.status === "scheduled" && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-xs text-[#666]">Start</label>
                  <Input type="time" value={editingShift.start_time} onChange={(e) => setEditingShift({...editingShift, start_time: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#666]">End</label>
                  <Input type="time" value={editingShift.end_time} onChange={(e) => setEditingShift({...editingShift, end_time: e.target.value})} />
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="text-xs text-[#666]">Note (optional)</label>
              <Input value={editingShift.note || ""} onChange={(e) => setEditingShift({...editingShift, note: e.target.value})} />
            </div>

            {editingShift.status === "scheduled" && (
              <div className="text-xs text-[#666] mb-3">Duration: <b>{hoursBetween(editingShift.start_time, editingShift.end_time).toFixed(2)}h</b> {hoursBetween(editingShift.start_time, editingShift.end_time) > 6 && <span className="text-[#92400E]">(-0.5h auto lunch)</span>}</div>
            )}

            <div className="flex gap-2">
              <Button onClick={saveShift} className="flex-1 bg-[#1A5E1F]">Save</Button>
              {editingShift.id && <Button onClick={deleteShift} variant="outline" className="text-[#A61B1B] border-[#A61B1B]">Delete</Button>}
              <Button onClick={() => setEditingShift(null)} variant="outline">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Department section renderer — extracted so each dept row + employees render as a clean keyed unit
function DeptGroup({ dept, emps, summary, shiftsMap, DAYS, openCell, copyPrevWeek, fmtSimple, hideEmployee, deptHasShifts, scheduledCount }) {
  // Print-safe: hide entire dept block from print if nobody is scheduled that week
  const deptPrintHide = deptHasShifts ? "" : "print:hidden";
  return (
    <>
      <tr className={deptPrintHide}>
        <td colSpan={8} className={`${DEPT_COLORS[dept]} border border-black px-2 py-1 font-extrabold text-sm uppercase tracking-wide print:break-inside-avoid`}>
          {dept}
          <span className="float-right text-xs font-normal">
            {/* On-screen: hours · $cost · headcount */}
            <span className="print:hidden">{summary.hours || 0}h · ${(summary.cost || 0).toFixed(2)} · {emps.length} {emps.length === 1 ? "person" : "people"}</span>
            {/* Print: headcount only, no money/hours */}
            <span className="hidden print:inline">{scheduledCount} {scheduledCount === 1 ? "person" : "people"}</span>
          </span>
        </td>
      </tr>
      {emps.map(emp => {
        const empHasShifts = DAYS.some((_, i) => shiftsMap[`${emp.code}-${i}`]);
        const rowPrintHide = empHasShifts ? "" : "print:hidden";
        return (
        <tr key={emp.code} className={`hover:bg-gray-50 ${rowPrintHide}`}>
          <td className="border border-black px-2 py-1.5 font-bold uppercase text-sm" data-testid={`row-${emp.code}`}>
            <div className="flex items-center justify-between">
              <span>{emp.name}{emp.pay_category === "cash" && <span className="ml-1 text-[9px] font-normal normal-case bg-green-100 text-green-800 px-1 rounded align-middle print:hidden">CASH</span>}</span>
              <div className="flex items-center gap-1 print:hidden">
                <button onClick={() => copyPrevWeek(emp.code)} className="text-[10px] text-[#666] hover:text-[#A61B1B] px-1" title="Repeat last week for this employee">↻</button>
                <button onClick={() => hideEmployee(emp.code, emp.name)} className="text-[10px] text-[#92400E] hover:text-[#A61B1B] hover:bg-amber-100 px-1.5 rounded font-bold" title="Hide from schedule (won't print)" data-testid={`hide-${emp.code}`}>×</button>
              </div>
            </div>
            <div className="text-[10px] font-normal text-[#666] normal-case print:hidden schedule-pay-info">
              {emp.pay_type === "salaried" ? `Salary $${emp.weekly_salary}/wk` : `$${emp.hourly_rate}/hr${emp.ot_eligible ? " · OT" : ""}`}
              {emp.scheduled_hours > 0 && <span className="ml-1 font-bold">· {emp.scheduled_hours}h{emp.ot_hours > 0 ? ` (${emp.ot_hours}h OT)` : ""}</span>}
            </div>
          </td>
          {DAYS.map((_, dayIdx) => {
            const s = shiftsMap[`${emp.code}-${dayIdx}`];
            let content;
            let cellClass = "border border-black px-1 py-1 text-center text-sm cursor-pointer hover:bg-yellow-50 print:hover:bg-transparent";
            if (!s) {
              content = <span className="text-gray-300 print:text-transparent">+</span>;
            } else if (s.status === "off") {
              content = <span className="font-black tracking-widest text-[#1A1A1A] text-base print:text-black">OFF</span>;
              cellClass += " bg-gray-100 print:bg-gray-100";
            } else if (s.status === "sick") {
              content = <span className="font-extrabold text-[#A61B1B]">SICK</span>;
              cellClass += " bg-red-50";
            } else if (s.status === "call_off") {
              content = <span className="font-extrabold text-[#92400E]">CALL OFF</span>;
              cellClass += " bg-yellow-50";
            } else {
              content = <span className="font-medium whitespace-nowrap">{fmtSimple(s.start_time)}-{fmtSimple(s.end_time)}</span>;
            }
            return (
              <td key={dayIdx} className={cellClass} onClick={() => openCell(emp.code, dayIdx)} data-testid={`cell-${emp.code}-${dayIdx}`}>{content}</td>
            );
          })}
        </tr>
        );
      })}
    </>
  );
}
